// ANA-R3-GEOM-04 — floating toolbar by the selected defect to enter + drive the
// in-place geometry editor (move/add/remove vertices → explicit Save).
//
// Two states, both rendered as a small floating toolbar near the selection
// (consistent with SB6-ANA-VIEW-05):
//
//   IDLE (a defect is selected, editing not yet started)
//     → a single "Edit geometry" button, gated by `editAllowed`.
//
//   EDITING (the shared PolygonEditor is mounted via EditGeometryOverlay)
//     → Save (commit → PATCH), Cancel (discard → restore persisted shape),
//       Undo (drives the primitive's own undo — AC3), and mode-convert toggles
//       (rect ↔ polygon ↔ polyline; AC4 legacy box → polygon).
//
// The toolbar floats near the selected defect by projecting the defect's bbox
// centroid through the live OSD viewport API; it falls back to the viewer cell's
// top-left when no projection is available (pre-open / tests).
//
// COMMIT CONVERSION (step 3). The editor holds NORMALISED 0..1 vertices; Save
// serialises them (with the GeoJSON closing duplicate for closed shapes via
// `serialisePoints`) and multiplies by the image dims to a flat PIXEL path,
// which is what `UpdateDefectGeometryBody.path` expects. The same serialised
// NORMALISED path drives the optimistic overlay update so the new outline shows
// instantly; `area`/`bbox` come back from the server on refetch.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Stack, Tooltip } from "@mui/material";
import {
  serialisePoints,
  toPolygon,
  toPolyline,
  toRect,
  type PolygonEditorApi,
  type PolygonValue,
} from "@aeriosiq/ui";
import { useEditGeometryStore } from "../state/editGeometryStore.js";
import { useViewportApiStore } from "../state/viewportApiStore.js";
import {
  useUpdateDefectGeometryMutation,
  type UpdateDefectGeometryVars,
} from "../../defects/queries.js";
import { useAuditLogAppendMutation } from "../../audit/queries.js";
import { CheckIcon, PencilIcon, Undo2Icon, XIcon } from "@aeriosiq/ui/icons";

/** Minimal shape the toolbar needs from the selected defect. */
export interface EditableDefect {
  id: string;
  imageId: string;
  path?: number[] | null;
  bbox?: { x: number; y: number; w: number; h: number } | null;
}

export interface EditGeometryToolbarProps {
  /** The currently-selected defect (or null — only defects are editable). */
  selectedDefect: EditableDefect | null;
  /** D-5 gate; when false the Edit-geometry affordance is hidden (AC5). */
  editAllowed: boolean;
  /** Owning data acquisition (for the mutation + audit row). */
  inspectionId: string;
  /** Source image pixel dims — normalised ↔ pixel conversion on Save. */
  imageWidth: number;
  imageHeight: number;
  /** Live PolygonEditor API published by EditGeometryOverlay (Save/Undo). */
  editorApi: PolygonEditorApi | null;
}

export function EditGeometryToolbar({
  selectedDefect,
  editAllowed,
  inspectionId,
  imageWidth,
  imageHeight,
  editorApi,
}: EditGeometryToolbarProps): React.ReactElement | null {
  const editingDefectId = useEditGeometryStore((s) => s.defectId);
  const mode = useEditGeometryStore((s) => s.mode);
  const points = useEditGeometryStore((s) => s.points);
  const begin = useEditGeometryStore((s) => s.begin);
  const cancel = useEditGeometryStore((s) => s.cancel);
  const finish = useEditGeometryStore((s) => s.finish);
  const setDraft = useEditGeometryStore((s) => s.setDraft);
  const viewportApi = useViewportApiStore((s) => s.api);

  const geometryMutation = useUpdateDefectGeometryMutation(inspectionId);
  const audit = useAuditLogAppendMutation();

  const [saving, setSaving] = useState(false);

  const editing = editingDefectId !== null;

  // Keep a live ref to the editor API so the window-level Ctrl+Z listener
  // (installed once while editing) always sees the latest handle.
  const editorApiRef = useRef<PolygonEditorApi | null>(editorApi);
  editorApiRef.current = editorApi;

  // AC3 — route Ctrl+Z to the PRIMITIVE's own undo while editing. We install a
  // capture-phase window listener that pre-empts the global story-064 Ctrl+Z
  // handler (which would otherwise pop the per-image defect stack). No parallel
  // geometry stack: the editor owns geometry undo.
  useEffect(() => {
    if (!editing) return;
    const onKey = (e: KeyboardEvent) => {
      const isMod = e.ctrlKey || e.metaKey;
      if (!isMod || e.shiftKey) return;
      if (e.key !== "z" && e.key !== "Z") return;
      const api = editorApiRef.current;
      if (!api) return;
      e.preventDefault();
      e.stopPropagation();
      api.undo();
    };
    // Capture phase so we run before the bubble-phase global handler.
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
    };
  }, [editing]);

  // Project a screen anchor for the toolbar from the defect's bbox centroid.
  const anchor = useMemo<{ left: number; top: number }>(() => {
    const fallback = { left: 8, top: 8 };
    const bbox = selectedDefect?.bbox;
    if (!viewportApi || !bbox) return fallback;
    try {
      const cx = bbox.x + bbox.w / 2;
      const cy = bbox.y; // anchor just above the box
      const sp = viewportApi.imageToScreen({ x: cx, y: cy });
      // Clamp into a sane band; the parent cell clips overflow anyway.
      const left = Math.max(8, sp.x - 60);
      const top = Math.max(8, sp.y - 44);
      return { left, top };
    } catch {
      return fallback;
    }
    // Re-project when the viewport changes — `viewportApi.version` bumps on
    // every OSD pan/zoom/resize, which is exactly when the anchor must move.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewportApi, viewportApi?.version, selectedDefect?.bbox]);

  const handleBeginEdit = useCallback(() => {
    if (!selectedDefect) return;
    begin({
      defectId: selectedDefect.id,
      imageId: selectedDefect.imageId,
      path: selectedDefect.path,
      bbox: selectedDefect.bbox,
    });
  }, [selectedDefect, begin]);

  const convert = useCallback(
    (target: "rect" | "polygon" | "polyline") => {
      const value: PolygonValue = { mode, points };
      const next =
        target === "rect"
          ? toRect(value)
          : target === "polygon"
            ? toPolygon(value)
            : toPolyline(value);
      setDraft(next.mode, next.points);
    },
    [mode, points, setDraft],
  );

  const handleSave = useCallback(async () => {
    if (!editing || !editingDefectId) return;
    if (points.length === 0) return;
    if (imageWidth <= 0 || imageHeight <= 0) return;
    // Normalised flat path WITH the closing duplicate for closed shapes.
    const normFlat = serialisePoints({ mode, points });
    if (normFlat.length < 4) return;
    // → PIXEL flat path (× image dims) for the wire body.
    const pixelPath = normFlat.map((v, i) =>
      i % 2 === 0 ? v * imageWidth : v * imageHeight,
    );
    const vars: UpdateDefectGeometryVars = {
      defectId: editingDefectId,
      body: { path: pixelPath },
      optimisticPath: normFlat,
    };
    setSaving(true);
    try {
      await geometryMutation.mutateAsync(vars);
      // Best-effort audit row (AC5) — failure degrades, never blocks the save.
      try {
        await audit.mutateAsync({
          entityType: "defect",
          entityId: editingDefectId,
          dataAcquisitionId: inspectionId,
          action: "update",
          before: { geometry: "edited" },
          after: { geometry: "saved", mode },
        });
      } catch (e: unknown) {
        console.warn("[GEOM-04] audit-log append failed (degraded)", e);
      }
      finish();
    } catch {
      // The mutation rolled the optimistic overlay back; stay in edit mode so
      // the analyst can retry or cancel (AC2 — nothing persisted).
    } finally {
      setSaving(false);
    }
  }, [
    editing,
    editingDefectId,
    points,
    mode,
    imageWidth,
    imageHeight,
    geometryMutation,
    audit,
    inspectionId,
    finish,
  ]);

  // IDLE — a defect is selected and editing is allowed: offer "Edit geometry".
  if (!editing) {
    if (!selectedDefect || !editAllowed) return null;
    return (
      <Box
        data-testid="edit-geometry-toolbar"
        role="toolbar"
        aria-label="Edit defect geometry"
        sx={floatingSx(anchor)}
      >
        <Tooltip title="Edit this defect's geometry">
          <Button
            size="small"
            variant="contained"
            startIcon={<PencilIcon fontSize="small" />}
            onClick={handleBeginEdit}
            data-testid="edit-geometry-start"
          >
            Edit geometry
          </Button>
        </Tooltip>
      </Box>
    );
  }

  // EDITING — Save / Cancel / Undo + mode-convert toggles.
  const canSave = points.length >= (mode === "polyline" ? 2 : 3) && !saving;
  return (
    <Box
      data-testid="edit-geometry-toolbar"
      role="toolbar"
      aria-label="Edit defect geometry"
      data-edit-mode={mode}
      sx={floatingSx(anchor)}
    >
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Tooltip title="Save geometry">
          <span>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<CheckIcon fontSize="small" />}
              disabled={!canSave}
              onClick={() => {
                void handleSave();
              }}
              data-testid="edit-geometry-save"
            >
              Save
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Discard changes">
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<XIcon fontSize="small" />}
            onClick={() => {
              cancel();
            }}
            data-testid="edit-geometry-cancel"
          >
            Cancel
          </Button>
        </Tooltip>
        <Tooltip title="Undo last edit (Ctrl+Z)">
          <span>
            <Button
              size="small"
              variant="text"
              color="inherit"
              disabled={!editorApi?.canUndo}
              onClick={() => {
                editorApiRef.current?.undo();
              }}
              data-testid="edit-geometry-undo"
              aria-label="Undo last edit"
              sx={{ minWidth: 0, px: 0.75 }}
            >
              <Undo2Icon fontSize="small" />
            </Button>
          </span>
        </Tooltip>
        {/* Mode-convert toggles — rect ↔ polygon ↔ polyline (AC4). */}
        {mode !== "polygon" ? (
          <Tooltip title="Convert to polygon">
            <Button
              size="small"
              variant="text"
              onClick={() => {
                convert("polygon");
              }}
              data-testid="edit-geometry-to-polygon"
            >
              Polygon
            </Button>
          </Tooltip>
        ) : null}
        {mode !== "rect" ? (
          <Tooltip title="Convert to rectangle">
            <Button
              size="small"
              variant="text"
              onClick={() => {
                convert("rect");
              }}
              data-testid="edit-geometry-to-rect"
            >
              Rect
            </Button>
          </Tooltip>
        ) : null}
      </Stack>
    </Box>
  );
}

function floatingSx(anchor: { left: number; top: number }) {
  return {
    position: "absolute" as const,
    left: anchor.left,
    top: anchor.top,
    // Above the OSD canvas, read overlay (1-2), readouts (4), marquee (5),
    // DrawOverlay (10) and the EditGeometryOverlay (11) so the buttons stay
    // clickable mid-edit. Same band as ViewerToolbar (12).
    zIndex: 13,
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    p: 0.5,
    borderRadius: 1,
    backgroundColor: "background.paper",
    boxShadow: 3,
  };
}
